import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useGenerateApplications } from '../../api/queries';
import styles from './ApplicationsView.module.css';

type Project = {
  title: string;
  description: string;
  expected_outcomes?: string;
  // Backend has been seen returning either an array of steps or a single
  // string with the whole plan in one paragraph — accept both.
  implementation_steps?: string[] | string;
};

export function ApplicationsView() {
  const mut = useGenerateApplications();
  const [open, setOpen] = useState<number | null>(0);

  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mut.isPending) return <LoadingState label="Buscando aplicaciones prácticas…" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;

  const app = data.content?.practical_applications ?? {};
  const ideas: Project[] = app.project_ideas ?? [];
  const trends: string = app.future_trends ?? '';

  return (
    <div className={styles.view}>
      {ideas.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionLabel}>Ideas de proyecto</h3>
          <div className={styles.cards}>
            {ideas.map((p, i) => {
              const isOpen = open === i;
              return (
                <article key={i} className={clsx(styles.card, isOpen && styles.cardOpen)}>
                  <button
                    type="button"
                    className={styles.head}
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                  >
                    <span className={styles.title}>{p.title}</span>
                    <ChevronDown
                      size={18}
                      strokeWidth={2.2}
                      className={clsx(styles.chev, isOpen && styles.chevOpen)}
                    />
                  </button>
                  <p className={styles.body}>{p.description}</p>
                  {isOpen && (
                    <div className={styles.detail}>
                      {p.expected_outcomes && (
                        <div className={styles.detailBlock}>
                          <h4>Resultados esperados</h4>
                          <p>{p.expected_outcomes}</p>
                        </div>
                      )}
                      {p.implementation_steps && (
                        <div className={styles.detailBlock}>
                          <h4>Cómo abordarlo</h4>
                          {Array.isArray(p.implementation_steps) ? (
                            <ol className={styles.steps}>
                              {p.implementation_steps.map((s, j) => (
                                <li key={j}>{s.replace(/^\d+\.\s*/, '')}</li>
                              ))}
                            </ol>
                          ) : (
                            <p>{p.implementation_steps}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {trends && (
        <section className={styles.section}>
          <h3 className={styles.sectionLabel}>Tendencias futuras</h3>
          <div className={styles.trendsBox}>
            <ReactMarkdown>{trends}</ReactMarkdown>
          </div>
        </section>
      )}
    </div>
  );
}
